/**
 * AgeGroup関連の日付計算ユーティリティ
 */
class AgeGroupDateUtils {
  /**
   * 先月の月初を取得
   * @returns 先月の月初（Dateオブジェクト）
   */
  static getPreviousMonthStart(): Date {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    return new Date(currentYear, currentMonth - 1, 1);
  }

  /**
   * 前年の先月の月初を取得
   * @returns 前年の先月の月初（Dateオブジェクト）
   */
  static getPreviousYearPreviousMonthStart(): Date {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    return new Date(currentYear - 1, currentMonth - 1, 1);
  }

  /**
   * 先月を含む36ヶ月分の開始日（35ヶ月前の月初）を取得
   * @returns 35ヶ月前の月初（Dateオブジェクト）
   */
  static getThreeYearsAgoStart(): Date {
    const previousMonthStart = this.getPreviousMonthStart();
    const startDate = new Date(previousMonthStart.getFullYear(), previousMonthStart.getMonth(), 1);
    startDate.setMonth(startDate.getMonth() - 35);
    return startDate;
  }

  /**
   * 日付をYYYY-MM-01形式の文字列に変換
   * @param date Dateオブジェクト
   * @returns YYYY-MM-01形式の文字列
   */
  static formatToYMD(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
  }

  /**
   * 日付から年を取得
   * @param date Dateオブジェクト
   * @returns 年
   */
  static getYear(date: Date): number {
    return date.getFullYear();
  }

  /**
   * 日付から月を取得（0-11）
   * @param date Dateオブジェクト
   * @returns 月（0-11）
   */
  static getMonth(date: Date): number {
    return date.getMonth();
  }

  /**
   * 日付から月を取得（1-12）
   * @param date Dateオブジェクト
   * @returns 月（1-12）
   */
  static getMonthOneBased(date: Date): number {
    return date.getMonth() + 1;
  }

  /**
   * データポイントから日付の月初を取得
   * @param pointDate データポイントの日付（Dateオブジェクト）
   * @returns 月初（Dateオブジェクト）
   */
  static getMonthStart(pointDate: Date): Date {
    return new Date(pointDate.getFullYear(), pointDate.getMonth(), 1);
  }

  /**
   * データポイントが指定された期間内かどうかを判定
   * @param pointDate データポイントの日付（Dateオブジェクト）
   * @param startDate 開始日（Dateオブジェクト）
   * @param endDate 終了日（Dateオブジェクト）
   * @returns 期間内の場合true
   */
  static isInRange(pointDate: Date, startDate: Date, endDate: Date): boolean {
    const pointMonthStart = this.getMonthStart(pointDate);
    return pointMonthStart >= startDate && pointMonthStart <= endDate;
  }

  /**
   * 先月と前年の先月の情報を取得
   * @returns 先月と前年の先月の情報（年、月、Dateオブジェクト）
   */
  static getComparisonMonthInfo(): {
    previousMonth: { year: number; month: number; date: Date };
    previousYearPreviousMonth: { year: number; month: number; date: Date };
  } {
    const previousMonthStart = this.getPreviousMonthStart();
    const previousYearPreviousMonthStart = this.getPreviousYearPreviousMonthStart();

    return {
      previousMonth: {
        year: this.getYear(previousMonthStart),
        month: this.getMonth(previousMonthStart),
        date: previousMonthStart,
      },
      previousYearPreviousMonth: {
        year: this.getYear(previousYearPreviousMonthStart),
        month: this.getMonth(previousYearPreviousMonthStart),
        date: previousYearPreviousMonthStart,
      },
    };
  }

  /**
   * データポイントから指定された年月のデータを検索
   * @param sortedData ソート済みのデータポイント配列
   * @param year 年
   * @param month 月（0-11）
   * @returns 見つかったデータポイント、見つからない場合はundefined
   */
  static findDataByYearMonth<T extends { data_date: { date: string } }>(
    sortedData: T[],
    year: number,
    month: number,
  ): T | undefined {
    return sortedData.find((point) => {
      const pointDate = new Date(point.data_date.date);
      return (
        this.getYear(pointDate) === year &&
        this.getMonth(pointDate) === month
      );
    });
  }
}

export { AgeGroupDateUtils };

